// Custom word packs — created by the user via the "+ Custom" tile in the
// pack picker. Each pack holds 5 words; audio is synthesized at play time
// by the existing `speak()` audio path (audioUrl=null triggers browser
// TTS fallback), and images are generated client-side as a data: URL of
// a coloured initial-letter tile so no external image API is needed.

import type { WordPack } from "../data/word-packs";

const STORAGE_KEY = "wr_custom_packs_v1";

export interface CustomPackInput {
  title: string;
  words: { english: string; vietnamese?: string; sentence?: string }[];
}

function loadAll(): WordPack[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw) as WordPack[];
    return Array.isArray(arr) ? arr : [];
  } catch {
    return [];
  }
}

function saveAll(packs: WordPack[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(packs)); } catch { /* ignore */ }
}

export function listCustomPacks(): WordPack[] {
  return loadAll();
}

export function getCustomPack(id: string): WordPack | undefined {
  return loadAll().find((p) => p.id === id);
}

export function deleteCustomPack(id: string) {
  saveAll(loadAll().filter((p) => p.id !== id));
}

/** Picks a colour from a palette by hashing the word — same word always
 *  gets the same tile colour, which makes the gallery feel consistent. */
const TILE_PALETTE = [
  "#ff5fa7", "#a15cff", "#6bd0ff", "#ffd166", "#15b67a",
  "#ff8a3d", "#2563eb", "#c084fc", "#22c55e", "#ef4444",
];
function colorFor(word: string): string {
  let h = 0;
  for (let i = 0; i < word.length; i++) h = (h * 31 + word.charCodeAt(i)) >>> 0;
  return TILE_PALETTE[h % TILE_PALETTE.length]!;
}

/** Generate a 256×256 PNG data URL with the word's first letter centred on
 *  a soft gradient tile. Self-contained; no network calls. */
function makeImageDataUrl(word: string): string {
  const c = document.createElement("canvas");
  c.width = 256; c.height = 256;
  const ctx = c.getContext("2d")!;
  const color = colorFor(word);
  // Soft radial backdrop.
  const grad = ctx.createRadialGradient(128, 110, 30, 128, 128, 140);
  grad.addColorStop(0, "#ffffff");
  grad.addColorStop(0.6, color);
  grad.addColorStop(1, color);
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(128, 128, 124, 0, Math.PI * 2);
  ctx.fill();
  // Letter.
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.font = "900 168px Nunito, Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const letter = (word.trim()[0] ?? "?").toUpperCase();
  ctx.fillText(letter, 128, 142);
  return c.toDataURL("image/png");
}

/** Turn user-entered words into a WordPack and persist it. Returns the
 *  newly-created pack (caller can navigate to /play and find it in the
 *  picker). */
export function createCustomPack(input: CustomPackInput): WordPack {
  const id = `pack_custom_${Date.now().toString(36)}`;
  const pack: WordPack = {
    id,
    title: input.title.trim() || "Custom Pack",
    subtitle: `Your custom ${input.words.length}-word set`,
    theme: "candy",
    timeOfDay: "afternoon",
    orderIndex: 100,
    words: input.words.slice(0, 5).map((w, i) => {
      const english = w.english.trim();
      return {
        id: `${id}__w${i}`,
        english,
        vietnamese: (w.vietnamese ?? "").trim(),
        sentence: (w.sentence ?? `This is a ${english}.`).trim(),
        imageUrl: makeImageDataUrl(english),
        // audioUrl=null → the engine's `speak()` falls back to browser TTS
        // via `speechSynthesis`. No server / external API needed.
        audioUrl: null,
        sentenceAudioUrl: null,
        orderIndex: i,
      };
    }),
  };
  const all = loadAll();
  all.push(pack);
  saveAll(all);
  return pack;
}
